import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { resolveAsset } from "../lib/assets";
import { attachVisualizer } from "../effects/visualizer";

/**
 * Audio player. Collapsible track bar with autoplay-on-gate, sticky volume,
 * shuffle, prev/next, scrubbable progress, and an optional Web Audio
 * visualizer. Plays a single <audio> element; tracks are cycled via JS.
 *
 * The caller passes a `gateOpened` promise so we can autoplay only after the
 * user's click (browser autoplay policy).
 */
export function mountAudioPlayer(
  cfg: ProfileConfig["audio"],
  gateOpened: Promise<void>,
): HTMLElement | null {
  if (!cfg.enabled || cfg.tracks.length === 0) return null;

  // Build an ordered play list (shuffled on mount if cfg.shuffle).
  const playlist = cfg.shuffle ? shuffle([...cfg.tracks]) : [...cfg.tracks];
  let idx = 0;

  const audio = new Audio();
  audio.preload = "metadata";
  audio.crossOrigin = "anonymous"; // needed if we want a visualizer

  // Sticky volume. Default is deliberately quiet (25%) — background audio on
  // first entry should never blow out someone's ears.
  const storedVol = safeStorageGet("bio:vol");
  audio.volume = storedVol != null ? clamp01(parseFloat(storedVol)) : 0.25;

  // ─── DOM ──────────────────────────────────────────────────────────────
  const wrap = el("div", { class: "audio-player", "data-collapsed": "false" });

  const cover = el("div", { class: "audio-player__cover" });
  const coverImg = el("img", { class: "audio-player__cover-img", alt: "", draggable: false });
  cover.append(coverImg);

  const info = el("div", { class: "audio-player__info" });
  const titleEl = el("div", { class: "audio-player__title" }, ["—"]);
  const timeRow = el("div", { class: "audio-player__times" }, [
    el("span", { class: "audio-player__cur" }, ["0:00"]),
    " / ",
    el("span", { class: "audio-player__dur" }, ["0:00"]),
  ]);
  const progress = el("div", { class: "audio-player__progress", role: "slider", "aria-label": "Seek", tabindex: "0" });
  const progressFill = el("div", { class: "audio-player__progress-fill" });
  progress.append(progressFill);
  info.append(titleEl, progress, timeRow);

  const controls = el("div", { class: "audio-player__controls" });
  const btnShuffle = iconBtn("shuffle", "Shuffle", cfg.shuffle ? "audio-player__btn--on" : "");
  const btnPrev = iconBtn("prev", "Previous");
  const btnPlay = iconBtn("play", "Play");
  const btnNext = iconBtn("next", "Next");
  const btnVol = iconBtn("vol", "Mute / unmute");
  // Horizontal volume slider. Bound to the HTMLAudioElement's volume.
  const volSlider = document.createElement("input");
  volSlider.type = "range";
  volSlider.min = "0";
  volSlider.max = "1";
  volSlider.step = "0.01";
  volSlider.value = String(audio.volume);
  volSlider.className = "audio-player__volume";
  volSlider.setAttribute("aria-label", "Volume");
  volSlider.setAttribute("data-tooltip", "Volume");
  volSlider.addEventListener("input", () => {
    const v = parseFloat(volSlider.value);
    if (Number.isFinite(v)) {
      audio.volume = v;
      if (audio.muted && v > 0) audio.muted = false;
    }
  });
  controls.append(btnShuffle, btnPrev, btnPlay, btnNext, btnVol, volSlider);

  const toggle = el("button", {
    class: "audio-player__toggle",
    type: "button",
    "aria-label": "Collapse audio player",
    "data-tooltip": "Hide player",
  }, ["▾"]);

  wrap.append(toggle, cover, info, controls);

  // Optional visualizer. Placement depends on cfg.visualizerExtent:
  //   "player"     — inside the cover (smallest, current default)
  //   "card"       — absolute-position inside the profile card
  //   "background" — full viewport, appended to <body>
  // (The old "border" extent was removed — it never traced the perimeter
  // cleanly and the simpler card/background modes cover the use case.)
  let visualizerStop: (() => void) | null = null;
  if (cfg.visualizer) {
    const canvas = el("canvas", {
      class: `audio-player__viz audio-player__viz--${cfg.visualizerExtent}`,
      "aria-hidden": "true",
    });
    canvas.style.setProperty("--viz-color", cfg.visualizerColor);
    canvas.style.setProperty("--viz-intensity", String(cfg.visualizerIntensity));
    canvas.style.setProperty("--viz-opacity", String(cfg.visualizerOpacity ?? 0.55));
    // Ensure the canvas has a concrete pixel size the moment it mounts —
    // if offsetWidth/Height is 0 when the draw loop first runs (common
    // inside rAF before layout), bars render to a 0-height canvas and
    // appear mid-screen as stubs. ResizeObserver keeps it synced.
    const syncSize = () => {
      const w = canvas.offsetWidth || canvas.clientWidth || 0;
      const h = canvas.offsetHeight || canvas.clientHeight || 0;
      if (w > 0) canvas.width = w;
      if (h > 0) canvas.height = h;
    };
    const resizeObs = new ResizeObserver(syncSize);
    requestAnimationFrame(() => {
      syncSize();
      resizeObs.observe(canvas);
    });
    if (cfg.visualizerExtent === "background") {
      document.body.appendChild(canvas);
    } else if (cfg.visualizerExtent === "card") {
      requestAnimationFrame(() => {
        const audioPane = document.querySelector(".cluster__pane--audio");
        const card = document.querySelector(".card");
        const host = audioPane ?? card;
        if (host) host.appendChild(canvas);
        else document.body.appendChild(canvas);
      });
    } else {
      cover.append(canvas);
    }
    visualizerStop = attachVisualizer(audio, canvas, {
      visualizerStyle: cfg.visualizerStyle,
      visualizerScale: cfg.visualizerScale,
      visualizerColor: cfg.visualizerColor,
      visualizerColor2: cfg.visualizerColor2,
      visualizerIntensity: cfg.visualizerIntensity,
      visualizerBaseline: cfg.visualizerBaseline,
    });
  }

  // ─── State plumbing ──────────────────────────────────────────────────
  // Generation counter for cover-color extraction. Each loadCurrent()
  // increments it; async extractions capture the current generation and
  // bail out if a newer track has loaded by the time they finish. This
  // prevents the "wrong color on fast track skips" race where the older
  // extraction would resolve last and overwrite the correct color.
  let coverGen = 0;

  const loadCurrent = () => {
    const track = playlist[idx];
    if (!track) return;
    audio.src = resolveAsset(track.src);
    titleEl.textContent = track.artist ? `${track.artist} — ${track.title}` : track.title;
    if (track.cover) {
      const coverUrl = resolveAsset(track.cover);
      coverImg.src = coverUrl;
      coverImg.style.display = "";
      // If configured, extract a primary color from the cover and apply
      // it as the visualizer accent. Errors (CORS taint, decode failure)
      // silently fall back to the admin-configured fixed color.
      if (
        cfg.visualizerColorSource === "cover-simple" ||
        cfg.visualizerColorSource === "cover-thief"
      ) {
        const gen = ++coverGen;
        void (async () => {
          try {
            // Use a fresh off-DOM probe image rather than reading the
            // visible coverImg — the visible one may still report .complete
            // / .naturalWidth for the PREVIOUS track for a tick after
            // setting src, causing extractCoverColor to read stale pixels.
            const probe = new Image();
            probe.crossOrigin = "anonymous";
            probe.src = coverUrl;
            await probe.decode();
            if (gen !== coverGen) return; // newer track took over
            const { extractCoverColor } = await import("../effects/cover-color");
            const color = await extractCoverColor(
              probe,
              cfg.visualizerColorSource as "cover-simple" | "cover-thief",
            );
            if (color && gen === coverGen) {
              document
                .querySelectorAll<HTMLCanvasElement>(".audio-player__viz")
                .forEach((c) => c.style.setProperty("--viz-color", color));
            }
          } catch { /* ignored */ }
        })();
      }
    } else {
      coverImg.removeAttribute("src");
      coverImg.style.display = "none";
      // Bump generation so any in-flight extraction from a previous track
      // can't apply its color to this no-cover track.
      coverGen++;
    }
    wrap.style.setProperty("--audio-progress", "0%");
    progressFill.style.width = "0%";
  };

  const playPause = async () => {
    if (audio.paused) {
      try { await audio.play(); } catch { /* ignored */ }
    } else {
      audio.pause();
    }
  };

  const next = () => {
    idx = (idx + 1) % playlist.length;
    loadCurrent();
    void audio.play();
  };

  const prev = () => {
    idx = (idx - 1 + playlist.length) % playlist.length;
    loadCurrent();
    void audio.play();
  };

  btnPlay.addEventListener("click", playPause);
  btnNext.addEventListener("click", next);
  btnPrev.addEventListener("click", prev);
  btnShuffle.addEventListener("click", () => {
    // Re-shuffle the remaining queue, preserving current track at position 0.
    const current = playlist[idx];
    if (!current) return;
    const rest = playlist.filter((_, i) => i !== idx);
    shuffle(rest);
    playlist.length = 0;
    playlist.push(current, ...rest);
    idx = 0;
    btnShuffle.classList.toggle("audio-player__btn--on");
  });
  btnVol.addEventListener("click", () => {
    audio.muted = !audio.muted;
    btnVol.classList.toggle("audio-player__btn--on", audio.muted);
  });

  audio.addEventListener("play", () => setPlayIcon(btnPlay, true));
  audio.addEventListener("pause", () => setPlayIcon(btnPlay, false));
  audio.addEventListener("ended", next);
  audio.addEventListener("loadedmetadata", () => {
    const dur = wrap.querySelector(".audio-player__dur")!;
    dur.textContent = formatTime(audio.duration || 0);
  });
  audio.addEventListener("timeupdate", () => {
    const cur = wrap.querySelector(".audio-player__cur")!;
    cur.textContent = formatTime(audio.currentTime);
    const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
    progressFill.style.width = `${pct}%`;
    progress.setAttribute("aria-valuenow", String(Math.round(audio.currentTime)));
  });
  audio.addEventListener("volumechange", () => {
    safeStorageSet("bio:vol", String(audio.volume));
    // Keep the slider in sync if another control (mute button, hotkey) moved it.
    const sliderEl = wrap.querySelector<HTMLInputElement>(".audio-player__volume");
    if (sliderEl) sliderEl.value = String(audio.muted ? 0 : audio.volume);
  });

  // Progress click → seek.
  progress.addEventListener("click", (ev) => {
    const rect = progress.getBoundingClientRect();
    const ratio = clamp01((ev.clientX - rect.left) / rect.width);
    if (Number.isFinite(audio.duration)) audio.currentTime = ratio * audio.duration;
  });

  toggle.addEventListener("click", () => {
    const isCollapsed = wrap.dataset.collapsed === "true";
    wrap.dataset.collapsed = isCollapsed ? "false" : "true";
    toggle.textContent = isCollapsed ? "▾" : "▴";
    toggle.setAttribute(
      "aria-label",
      isCollapsed ? "Collapse audio player" : "Expand audio player",
    );
  });

  // ─── Autoplay on gate ────────────────────────────────────────────────
  loadCurrent();
  if (cfg.autoplayOnGate) {
    let started = false;
    const tryPlay = async () => {
      if (started) return;
      try {
        await audio.play();
        started = true;
        setPlayIcon(btnPlay, true);
      } catch {
        /* blocked — wait for a user gesture below */
      }
    };

    // First attempt: as soon as the gate opens. If the gate is disabled,
    // `gateOpened` resolves synchronously — meaning we try to autoplay on
    // page load without a user gesture, which most browsers block. In that
    // case we fall back to the FIRST user interaction anywhere on the page
    // (click, keydown, touch) and retry. One-shot listeners so they don't
    // linger after playback starts.
    gateOpened.then(tryPlay);

    // Gate click dispatches `bio:user-gesture` SYNCHRONOUSLY from its
    // click handler, before any setTimeout / promise resolves. Calling
    // tryPlay here means audio.play() runs within the user-gesture call
    // stack, satisfying iOS Safari's autoplay-after-gesture requirement.
    // The pointerdown / touchstart listeners below remain as backup for
    // pages where the gate is disabled — they also fire synchronously
    // from the user's first tap anywhere on the page.
    const retryOnGesture = () => {
      void tryPlay();
      if (started) removeGestureListeners();
    };
    const events: Array<keyof DocumentEventMap> = [
      "pointerdown",
      "keydown",
      "touchstart",
    ];
    const removeGestureListeners = () => {
      for (const ev of events) document.removeEventListener(ev, retryOnGesture);
      document.removeEventListener("bio:user-gesture", retryOnGesture as EventListener);
    };
    for (const ev of events) {
      document.addEventListener(ev, retryOnGesture, { passive: true });
    }
    document.addEventListener("bio:user-gesture", retryOnGesture as EventListener);

    wrap.addEventListener("bio:teardown", removeGestureListeners);
  }

  // Cleanup hook — fired when the profile re-renders (admin preview edits)
  // or when the user navigates away. Must actually stop playback so we
  // don't end up with overlapping <audio> elements still holding a stream.
  wrap.addEventListener("bio:teardown", () => {
    try {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    } catch { /* best-effort */ }
    visualizerStop?.();
    // Remove any canvases we placed outside the player wrap.
    document.querySelectorAll(".audio-player__viz--card, .audio-player__viz--background").forEach((n) => n.remove());
  });

  return wrap;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function iconBtn(icon: string, label: string, extraClass = ""): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = `audio-player__btn audio-player__btn--${icon} ${extraClass}`.trim();
  b.setAttribute("aria-label", label);
  b.setAttribute("data-tooltip", label);
  b.innerHTML = iconSvg(icon);
  return b;
}

function iconSvg(kind: string): string {
  const common = 'viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"';
  switch (kind) {
    case "play":
      return `<svg ${common}><path d="M8 5v14l11-7z"/></svg>`;
    case "pause":
      return `<svg ${common}><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>`;
    case "prev":
      return `<svg ${common}><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/></svg>`;
    case "next":
      return `<svg ${common}><path d="M16 6h2v12h-2zM6 18l8.5-6L6 6z"/></svg>`;
    case "shuffle":
      return `<svg ${common}><path d="M10.59 9.17L6.12 4.7l-1.41 1.41 4.47 4.47 1.41-1.41zM14.5 4l2.04 2.04L4.7 17.88l1.41 1.41L17.95 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/></svg>`;
    case "vol":
      return `<svg ${common}><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05a4.5 4.5 0 0 0 2.5-4.02z"/></svg>`;
    default:
      return "";
  }
}

function setPlayIcon(btn: HTMLElement, playing: boolean) {
  btn.innerHTML = iconSvg(playing ? "pause" : "play");
  btn.setAttribute("aria-label", playing ? "Pause" : "Play");
  btn.setAttribute("data-tooltip", playing ? "Pause" : "Play");
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function safeStorageGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeStorageSet(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* private mode, ignored */ }
}

// Visualizer implementation is in src/profile/effects/visualizer.ts —
// this file just imports it.
